import { zodResolver } from "@hookform/resolvers/zod";
import merge from "lodash.merge";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ProposedProjectController } from "services/proposedProject/controller";
import { FormRenderer, useAccordionWizard } from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import { removeEmptyValues } from "../../../loanDetails/elements/borrower/LoanDetailsBorrower";
import { getEventItemModel } from "../eventChart/EventItem";
import { scheduleFormFields } from "./ScheduleFormFields";

const proposedProjectController = ProposedProjectController();

const scheduleSchema = z
  .object({
    gantt_chart: z.boolean(),
    filename: z.any(),
    events: z.array(
      z
        .object({
          id: z.any().nullable(),
          name: z.string().nullable(),
          status: z.string().nullable(),
          start_date: z.string().nullable(),
          end_date: z.string().nullable(),
        })
        .partial()
        .passthrough()
        .refine(
          (data) => {
            return (
              !data.start_date ||
              !data.end_date ||
              new Date(data.start_date) <= new Date(data.end_date)
            );
          },
          {
            message: "The end date cannot be before the start date",
            path: ["end_date"],
          }
        )
    ),
  })
  .passthrough();

const schema = z.object({
  construction_schedule: scheduleSchema,
  development_schedule: scheduleSchema,
});

const getScheduleModel = () => ({
  construction_schedule: {
    gantt_chart: false,
    filename: "",
    events: [getEventItemModel()],
  },
  development_schedule: {
    gantt_chart: false,
    filename: "",
    events: [getEventItemModel()],
  },
});

export const ScheduleKey = "get-schedule";
const Schedule = () => {
  const form = useForm({
    defaultValues: getScheduleModel(),
    resolver: zodResolver(schema),
  });
  const params = useParams();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { next } = useAccordionWizard();
  const { handleFileFields } = useFileUpload();
  const queryKey = [ScheduleKey, { id: params.id }];
  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: () => proposedProjectController.getSchedules(params.id),
    onSuccess: (data) => {
      form.reset(merge(getScheduleModel(), data));
    },
  });

  const handleScheduleSubmit = async (values) => {
    return handleFileFields(values).then((values) => {
      values = removeEmptyValues(values);
      return proposedProjectController.updateSchedules({
        ...values,
        opportunityId: params.id,
      });
    });
  };

  const { mutate, isLoading: postLoading } = useMutation(handleScheduleSubmit, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      showToast("Project Schedule updated!", "success");
      next();
    },
  });

  return (
    <AccordionForm
      accordionKey="schedule"
      accordionTitle="Project Schedule"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <FormRenderer fields={scheduleFormFields} />
    </AccordionForm>
  );
};

export default Schedule;
